import { computeCoachingConfidence, defaultCoachAdvice } from './coachAdvice';
import { clamp01, normalizeRange, titleCase, formatFeet, formatSignedMph } from './helpers';

function formatPct(value) {
  if (value == null || !Number.isFinite(value)) return '--';
  return `${Math.round(value)}%`;
}

export function classifyThrottleIssue({
  corner,
  referenceCorner = null,
}) {
  const appSpread = corner?.applicationSpreadFt ?? 0;
  const rampSpread = corner?.rampDistSpreadFt ?? 0;
  const hesitationCount = corner?.hesitationCount ?? 0;
  const delta = corner?.best3Delta || null;
  const appDelta = delta?.applicationDeltaFt ?? null;
  const exitSpeedDelta = delta?.exitSpeedDeltaMph ?? null;
  const eventCount = corner?.eventCount ?? 0;
  const smallSampleNoise = eventCount < 4 ? 0.16 : eventCount < 6 ? 0.06 : 0;

  if (eventCount < 2) {
    return {
      type: 'inconclusive',
      patternStrength: 0.1,
      outcomeCorrelation: 0,
      causalClarity: 0.1,
      signalNoise: 0.5,
      referenceCorner,
    };
  }

  // Hesitation (lift-reapply) is the highest-priority diagnosis
  if (hesitationCount >= 1) {
    return {
      type: 'hesitant_application',
      patternStrength: clamp01(0.5 + hesitationCount * 0.2),
      outcomeCorrelation: clamp01((exitSpeedDelta != null && exitSpeedDelta < 0 ? Math.abs(exitSpeedDelta) / 3 : 0) + 0.3),
      causalClarity: 0.85,
      signalNoise: smallSampleNoise + 0.05,
      referenceCorner,
    };
  }

  // Application point varies too much
  if (appSpread >= 22) {
    return {
      type: 'application_inconsistent',
      patternStrength: normalizeRange(appSpread, 15, 40),
      outcomeCorrelation: clamp01(((exitSpeedDelta != null && exitSpeedDelta < 0 ? Math.abs(exitSpeedDelta) : 0) / 2.5) + 0.3),
      causalClarity: 0.88,
      signalNoise: smallSampleNoise,
      referenceCorner,
    };
  }

  // Getting to throttle too late compared to best laps
  if (appDelta != null && appDelta > 15 && exitSpeedDelta != null && exitSpeedDelta < -0.5) {
    return {
      type: 'late_application',
      patternStrength: clamp01((normalizeRange(appDelta, 10, 35) + normalizeRange(Math.abs(exitSpeedDelta), 0.5, 3)) / 2),
      outcomeCorrelation: clamp01((normalizeRange(Math.abs(exitSpeedDelta), 0.5, 3) * 0.7) + 0.2),
      causalClarity: 0.8,
      signalNoise: smallSampleNoise,
      referenceCorner,
    };
  }

  // Ramp to full throttle is too slow/long
  if (rampSpread >= 25 || (delta?.rampDistDeltaFt ?? 0) > 20) {
    return {
      type: 'slow_ramp',
      patternStrength: clamp01(Math.max(
        normalizeRange(rampSpread, 18, 45),
        normalizeRange(delta?.rampDistDeltaFt ?? 0, 15, 40)
      )),
      outcomeCorrelation: clamp01((exitSpeedDelta != null && exitSpeedDelta < 0 ? Math.abs(exitSpeedDelta) / 3 : 0) + 0.2),
      causalClarity: 0.72,
      signalNoise: smallSampleNoise + 0.06,
      referenceCorner,
    };
  }

  // Stable / benchmark
  if ((corner?.score ?? 0) >= 88) {
    return {
      type: 'stable',
      patternStrength: normalizeRange(corner.score, 88, 98),
      outcomeCorrelation: 0.45,
      causalClarity: 0.72,
      signalNoise: 0.05,
      referenceCorner,
    };
  }

  return {
    type: 'mild_variation',
    patternStrength: 0.35,
    outcomeCorrelation: 0.2,
    causalClarity: 0.45,
    signalNoise: smallSampleNoise,
    referenceCorner,
  };
}

function buildObservation(corner, diagnosis, referenceLabel, user) {
  const delta = corner?.best3Delta || null;
  const eventNote = corner?.eventCount ? ` across ${corner.eventCount} throttle events` : '';
  const ff = (v) => formatFeet(v, user);
  const fm = (v) => formatSignedMph(v, user);

  switch (diagnosis.type) {
    case 'hesitant_application':
      return `Throttle hesitation detected in ${corner.label}${eventNote} — you apply throttle, lift, then reapply an average of ${corner.hesitationCount} time${corner.hesitationCount !== 1 ? 's' : ''} per lap with an average drop of ${formatPct(corner.avgHesitationDropPct)}. Application point is ${ff(corner.applicationAvgFt)} after minimum speed. ${delta?.exitSpeedDeltaMph != null && delta.exitSpeedDeltaMph < -0.3 ? `Exit speed is ${fm(delta.exitSpeedDeltaMph)} versus your ${referenceLabel.toLowerCase()}.` : ''}`;
    case 'application_inconsistent':
      return `Throttle application point spread is ${ff(corner.applicationSpreadFt)} in ${corner.label}${eventNote}. You are picking up the throttle at a different point each lap, varying between the earliest and latest application by roughly ${ff(corner.applicationSpreadFt)}. Ramp distance spread is ${ff(corner.rampDistSpreadFt)}.`;
    case 'late_application':
      return `Versus your ${referenceLabel.toLowerCase()}, you apply throttle ${ff(delta?.applicationDeltaFt)} later in ${corner.label}${eventNote}. ${delta?.exitSpeedDeltaMph != null ? `Exit speed is ${fm(delta.exitSpeedDeltaMph)} below your best.` : ''} Application spread is ${ff(corner.applicationSpreadFt)}, so the late pickup is consistent — not random.`;
    case 'slow_ramp':
      return `Distance from initial throttle to full throttle in ${corner.label} is ${ff(corner.rampDistAvgFt)} on average${eventNote}, with a spread of ${ff(corner.rampDistSpreadFt)}. ${delta?.rampDistDeltaFt != null && delta.rampDistDeltaFt > 10 ? `That is ${ff(delta.rampDistDeltaFt)} longer than your ${referenceLabel.toLowerCase()}.` : ''} ${delta?.exitSpeedDeltaMph != null && delta.exitSpeedDeltaMph < -0.3 ? `Exit speed is ${fm(delta.exitSpeedDeltaMph)} below your best.` : ''}`;
    case 'stable':
      return `${corner.label} has a repeatable throttle profile${eventNote}: application spread is ${ff(corner.applicationSpreadFt)}, ramp spread is ${ff(corner.rampDistSpreadFt)}, and no hesitations detected. ${corner.score != null ? `Consistency score: ${corner.score}/100.` : ''} This is a clean, well-calibrated throttle zone.`;
    case 'inconclusive':
      return `Only ${corner.eventCount || 'a few'} clean throttle events recorded in ${corner.label}. There is not enough data to identify a reliable throttle pattern yet.`;
    default:
      return `${corner.label} shows some throttle variation${eventNote} — application spread: ${ff(corner.applicationSpreadFt)}, ramp spread: ${ff(corner.rampDistSpreadFt)} — but no single issue clearly dominates the pattern yet.`;
  }
}

function buildInference(diagnosis) {
  switch (diagnosis.type) {
    case 'hesitant_application':
      return 'A throttle lift-and-reapply is one of the most costly patterns on corner exit. Each hesitation breaks the acceleration phase and unsettles the car\'s weight transfer. It usually means the car felt loose or you were unsure of the grip level at that point. The exit straight is where you convert cornering into lap time, so every hesitation costs speed all the way to the next braking zone. The fix is almost always about the line or the turn-in: if the car is pointed in the right direction at the apex, the throttle application will feel natural and confident.';
    case 'application_inconsistent':
      return 'An inconsistent throttle pickup point means you are starting the acceleration phase at a different place each lap. This makes exit speed unpredictable and prevents you from building a repeatable rhythm through the corner. The throttle application point is usually tied to where you feel the car is finished rotating — if the rotation point changes (because of entry speed, line, or braking), the throttle point will follow. Stabilizing the corner entry usually stabilizes the throttle.';
    case 'late_application':
      return 'You are waiting too long after the apex to begin accelerating compared to your fastest laps. Late throttle application means the car coasts through the exit phase, losing time that compounds down the entire following straight. Every car length of delayed throttle translates to roughly 0.5-1 mph less exit speed. The cause is usually one of: the car is still rotating at the apex (too much entry speed), the line is too tight (forcing you to wait for the car to straighten), or lack of confidence in the grip available.';
    case 'slow_ramp':
      return 'You are getting to the throttle on time, but taking too long to build to full application. A slow ramp from initial throttle to full throttle means you are leaving acceleration on the table through the exit. This is often a confidence issue — the car may have felt loose or pushed on previous laps, so you are being cautious with how quickly you add power. It can also indicate too much steering lock at the exit, which limits how much throttle the rear tires can handle.';
    case 'stable':
      return 'Throttle application point, ramp rate, and exit speed are all tightly grouped, which means your technique through the exit is well-calibrated. A consistent throttle zone like this is the hallmark of a driver who has the corner dialed in — the line sets up the throttle naturally. Use the feel and confidence you have here as a reference when working on corners that have throttle inconsistency.';
    default:
      return 'The throttle data shows some variation, but the pattern does not clearly point to one root cause yet. This can happen when multiple small factors overlap. More clean laps will help separate the signals and reveal the dominant issue.';
  }
}

function buildTitle(diagnosis, corner) {
  switch (diagnosis.type) {
    case 'hesitant_application':
      return `${corner.label} has throttle hesitation on exit`;
    case 'application_inconsistent':
      return `${corner.label} picks up throttle at different points`;
    case 'late_application':
      return `${corner.label} is late to throttle on exit`;
    case 'slow_ramp':
      return `${corner.label} ramps to full throttle too slowly`;
    case 'stable':
      return `${corner.label} is your throttle benchmark`;
    case 'inconclusive':
      return `${corner.label} needs more throttle data`;
    default:
      return `${corner.label} has mild throttle variation`;
  }
}

function buildType(diagnosis) {
  if (diagnosis.type === 'stable') return 'positive';
  if (diagnosis.type === 'inconclusive') return 'neutral';
  return 'warning';
}

function buildCornerCoaching({ corner, referenceCorner = null, referenceLabel = 'Best 3 laps', sourceUsed = 'brake', user }) {
  const diagnosis = classifyThrottleIssue({ corner, referenceCorner });
  const confidence = computeCoachingConfidence({
    sourceUsed,
    eventCount: corner?.eventCount ?? 0,
    patternStrength: diagnosis.patternStrength,
    outcomeCorrelation: diagnosis.outcomeCorrelation,
    causalClarity: diagnosis.causalClarity,
    signalNoise: diagnosis.signalNoise,
  });

  return {
    id: `${corner.cornerId}-throttle-${diagnosis.type}`,
    cornerId: corner.cornerId,
    title: buildTitle(diagnosis, corner),
    type: buildType(diagnosis),
    diagnosis: diagnosis.type,
    observation: confidence.score >= 35 ? buildObservation(corner, diagnosis, referenceLabel, user) : null,
    inference: confidence.score >= 50 ? buildInference(diagnosis) : null,
    recommendation: confidence.score >= 35 ? defaultCoachAdvice.getAdvice({
      domain: 'throttle',
      diagnosisType: diagnosis.type,
      confidenceLabel: confidence.label,
      corner,
      user,
    }) : null,
    confidence,
    priority: Math.round((diagnosis.patternStrength * 100) + (confidence.score * 0.6)),
    meta: `${titleCase(confidence.label)} confidence`,
    referenceLabel,
  };
}

function buildPositiveFallback(coaching, actionableIds) {
  const candidate = coaching
    .filter((card) => !actionableIds.has(card.id) && card.diagnosis !== 'inconclusive')
    .sort((a, b) => b.confidence.score - a.confidence.score)[0];
  if (!candidate) return null;
  return {
    ...candidate,
    type: 'positive',
    title: `${candidate.cornerId ? candidate.cornerId : 'Best corner'} is your strongest throttle zone`,
    diagnosis: 'best_available',
    recommendation: defaultCoachAdvice.getAdvice({
      domain: 'throttle',
      diagnosisType: 'best_available',
      confidenceLabel: candidate.confidence.label,
      corner: { label: candidate.cornerId },
    }),
  };
}

export function buildThrottleCoachCards({
  analysis,
  referenceMode = 'best-3',
  user,
}) {
  if (!analysis?.corners?.length) return [];

  const referenceLabel = referenceMode === 'best-lap' ? 'Best lap' : 'Best 3 laps';
  const sourceUsed = analysis.sourceUsed === 'longG' ? 'longG' : 'brake';
  const coaching = analysis.corners
    .map((corner) => buildCornerCoaching({
      corner,
      referenceCorner: referenceMode === 'best-lap' ? corner.bestLapReference : corner.best3Reference,
      referenceLabel,
      sourceUsed,
      user,
    }))
    .filter((card) => card.confidence.score >= 35);

  const allActionable = coaching
    .filter((card) => card.diagnosis !== 'stable' && card.diagnosis !== 'inconclusive')
    .sort((a, b) => b.priority - a.priority);
  const actionable = allActionable.slice(0, 2);

  const benchmark = coaching
    .filter((card) => card.diagnosis === 'stable')
    .sort((a, b) => b.confidence.score - a.confidence.score)[0];

  const positiveCard = benchmark
    || buildPositiveFallback(coaching, new Set(actionable.map((c) => c.id)));

  return [
    ...actionable,
    ...(positiveCard ? [positiveCard] : []),
  ];
}
